import { Injectable } from '@angular/core';

import { FoldersDataService } from '@services';
import { TreeViewNode } from '@shared/modules/tree-viewer/models';
import {
  FolderDataType,
  FolderPickerConfig,
  FolderPickerEntities,
  FolderPickerEntityFolder,
  FolderPickerItem, FolderPickerSelection
} from '@modules/management/modules/folders/model/folders.interfaces';

import { cloneDeep, each, filter, has, includes, map } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class FolderPickerService {
  static readonly folderIdPrefix: string = 'folder:';
  private pickerConfig: FolderPickerConfig;
  constructor(private foldersDataService: FoldersDataService) {}

  public getTreeNodes(config: FolderPickerConfig): TreeViewNode[] {
    if (!config) {
      return [];
    }

    this.pickerConfig = config;
    const nodeId: number = 0;
    const folderItems = config?.getFolderItems && config.getFolderItems() || [];
    const rootFolders = this.getRootFolders(config.entities, config.parentIds, folderItems, config.dataType);
    const nodes = this.buildNodeTree(config.selectedIds, rootFolders);
    const treeNodes: TreeViewNode[] = [];

    if (nodes?.length) {
      const isMultiselect = this.pickerConfig?.viewTree?.multiselect || !has(this.pickerConfig, 'viewTree.multiselect');
      if (this.pickerConfig.selectAllLabel && isMultiselect) {
        treeNodes.push({
          id: `${FolderPickerService.folderIdPrefix}${nodeId}`,
          label: this.pickerConfig.selectAllLabel,
          nodes,
          checked: this.isChecked(config.selectedIds, nodeId, true)
        });
      } else {
        treeNodes.push(...nodes);
      }
    }
    this.addRootEntities(treeNodes, config);

    return treeNodes;
  }

  private getRootFolders(entities: FolderPickerEntities, rejectedIds: FolderPickerSelection[], items: FolderPickerItem[], dataType: FolderDataType): FolderPickerEntityFolder[] {
    entities.folderItems = items;
    entities.folders = cloneDeep(this.foldersDataService.getFolders([dataType]));

    const rootFolders = this.buildFolderTree(entities, rejectedIds);
    this.defineFolderItems(entities, rejectedIds);

    return rootFolders;
  }

  private buildFolderTree(entities: FolderPickerEntities, rejectedIds: FolderPickerSelection[]): FolderPickerEntityFolder[] {
    const rootFolders: FolderPickerEntityFolder[] = [];
    const rejectAll = this.pickerConfig.getFolderId(rejectedIds?.[0]) === 0;

    if (!rejectAll) {
      each(entities.folders, (folder) => {
        const isRejected = includes(map(rejectedIds, (id) => this.pickerConfig.getFolderId(id)), folder.folderID);

        if (!isRejected) {
          const parentFolder = entities.folders[folder.parentID];

          if (parentFolder) {
            if (!parentFolder.children) {
              parentFolder.children = [];
            }

            parentFolder.children.push(folder);
          } else {
            rootFolders.push(folder);
          }
        }
      });
    }

    return rootFolders;
  }

  private defineFolderItems(entities: FolderPickerEntities, rejectedIds: FolderPickerSelection[]) {
    each(entities.folderItems, (folderItem) => {
      const folder = entities.folders[folderItem.folderID];
      const isRejected = includes(map(rejectedIds, (id) => this.pickerConfig.getItemId(id)), this.pickerConfig.getItemId(folderItem));

      if (folder && !isRejected) {
        if (!folder.folderItems) {
          folder.folderItems = [];
        }

        folder.folderItems.push(folderItem);
      }
    });
  }

  private isChecked(values: FolderPickerSelection[], id: number, isFolder: boolean = false): boolean {
    let ids: (string | number)[] = [];

    if (isFolder) {
      ids = map(values, (value) => this.pickerConfig.getFolderId(value));
    } else {
      ids = map(values, (value) => this.pickerConfig.getItemId(value));
    }

    // Filter out undefined/null but keep 0 (which is a valid folder ID)
    ids = map(filter(ids, (id) => id !== undefined && id !== null), (id) => {
      return isNaN(+id) ? id : +id;
    });

    return includes(ids, id);
  }

  private buildNodeTree(selectedFolderItems: FolderPickerSelection[], folders: FolderPickerEntityFolder[]): TreeViewNode[] {
    return filter(map(folders, (folder) => {
      let nodes = this.buildNodeTree(selectedFolderItems, folder.children) || [];

      each(folder?.folderItems, (folderItem) => {
        const itemId = this.pickerConfig.getItemId(folderItem);

        nodes.push({
          id: itemId,
          label: this.pickerConfig.getItemTitle(folderItem),
          checked: this.isChecked(selectedFolderItems, itemId)
        });
      });

      return {
        id: `${FolderPickerService.folderIdPrefix}${folder.folderID}`,
        label: folder.title,
        checked: this.isChecked(selectedFolderItems, folder.folderID, true),
        nodes,
        showFolderIcon: true
      };
    }));
  }

  private addRootEntities(treeNodes: TreeViewNode[], config: FolderPickerConfig) {
    each(filter(config?.entities?.folderItems, { folderID: 0 }), (item) => {
      const itemId = config.getItemId(item);
      treeNodes.push({
        id: itemId,
        label: this.pickerConfig.getItemTitle(item),
        checked: this.isChecked(config.selectedIds, itemId)
      })
    });
  }
}
